import React, { useEffect, useState } from 'react'
import { HiOutlineShieldCheck } from 'react-icons/hi2'
import type { PDFDocumentProxy } from 'pdfjs-dist'
import { toast } from 'sonner'
import { apiClient } from '../../common/apiClient'
import { useAuth } from '../../contexts/AuthContext'
import { sanitizePolicyHtml } from '../../common/sanitizePolicyHtml'
import { DocumentReader } from '../reader/DocumentReader'
import { fetchPdfDocument, usePdfPages } from '../reader/usePdfPages'

interface PolicyDetails {
  id: number
  name: string
  office: string
  file_url: string
  content_kind: 'richtext' | 'pdf' | 'docx'
  content_html: string | null
  myAcknowledgement: {
    id: number
    status: string
    read_at: string | null
    // Set when HR re-requested this policy through a reminder.
    reminded_at?: string | null
  } | null
}

interface PolicyReadModalProps {
  policyId: number
  /** In View As: whose status to show. Only the signed-in person can confirm. */
  viewAs?: string
  open: boolean
  onClose: () => void
  onConfirmed: (policyId: number) => void
}

const fmtLong = (d: string) =>
  new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })

export const PolicyReadModal: React.FC<PolicyReadModalProps> = ({
  policyId,
  viewAs,
  open,
  onClose,
  onConfirmed,
}) => {
  const { getAccessToken } = useAuth()
  const [policy, setPolicy] = useState<PolicyDetails | null>(null)
  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const pages = usePdfPages(pdfDoc)
  const { markReady } = pages

  // The viewer signs off when they have a pending acknowledgement — or when HR has
  // re-requested a policy they'd already read (reminded after their last confirmation).
  // Without the second case a re-read request would be unactionable: the employee gets the
  // email, opens the policy, and finds nothing to confirm.
  const ack = policy?.myAcknowledgement
  const needsReread = !!(
    ack?.reminded_at &&
    (!ack.read_at || new Date(ack.read_at) <= new Date(ack.reminded_at))
  )
  const canConfirm = !viewAs && (ack?.status === 'pending' || needsReread)
  const alreadyRead = ack?.status === 'read' && !needsReread
  const htmlOnly = policy?.content_kind === 'richtext' && !policy?.file_url

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await apiClient.get<PolicyDetails>(
          `/api/policies/${policyId}${viewAs ? `?email=${encodeURIComponent(viewAs)}` : ''}`
        )
        setPolicy(data)

        // Rich-text policies render from their stored PDF — the faithful render staff sign
        // off on. Only fall back to content_html if no PDF was stored (render failed).
        if (data.content_kind === 'richtext' && !data.file_url) return

        setPdfDoc(await fetchPdfDocument(`/api/policies/${policyId}/pdf`, await getAccessToken()))
      } catch (err) {
        console.error('Error loading policy:', err)
        setFailed(true)
        toast.error('Failed to load policy')
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyId])

  // HTML-fallback rich-text (no stored PDF) renders synchronously; once it's in the DOM,
  // run the scroll check so a short policy that fits without scrolling counts as read.
  useEffect(() => {
    if (htmlOnly && !isLoading) markReady()
  }, [htmlOnly, isLoading, markReady])

  const handleConfirm = async () => {
    try {
      await apiClient.post(`/api/policies/${policyId}/acknowledge`, {})
      onConfirmed(policyId)
    } catch {
      toast.error('Failed to record your confirmation')
    }
  }

  const readAt = ack?.read_at ? fmtLong(ack.read_at) : null
  const who = viewAs ? 'They' : 'You'
  const message = alreadyRead
    ? readAt
      ? `${who} confirmed this on ${readAt}.`
      : `${who}’ve already confirmed this policy.`
    : viewAs
      ? `Viewing as ${viewAs}. Only they can confirm this policy.`
      : 'Read-only. There’s nothing for you to confirm.'

  return (
    <DocumentReader
      open={open}
      onClose={onClose}
      noun="policy"
      icon={<HiOutlineShieldCheck className="h-5 w-5" />}
      eyebrow={
        <>
          <span>{policy ? `${policy.office} office` : 'Company policy'}</span>
          {needsReread && (
            <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
              Updated · HR has asked everyone to read this again
              {readAt ? ` (${viewAs ? 'they' : 'you'} last confirmed ${readAt})` : ''}
            </span>
          )}
        </>
      }
      title={policy?.name}
      pages={pages}
      pageCount={pdfDoc?.numPages}
      isLoading={isLoading}
      failed={failed}
      // Rich-text policies render their HTML directly. Content is sanitized server-side
      // on save AND re-sanitized here with DOMPurify (never trust the render sink).
      content={
        htmlOnly ? (
          <div
            className="policy-content max-w-none rounded-lg bg-white p-6 shadow-sm ring-1 ring-slate-200 sm:p-10"
            dangerouslySetInnerHTML={{ __html: sanitizePolicyHtml(policy.content_html) }}
          />
        ) : undefined
      }
      canConfirm={canConfirm}
      done={alreadyRead}
      message={message}
      onConfirm={handleConfirm}
    />
  )
}
